import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import { BankCardCodeType, IMemberBankInfo } from "../types/atmos";
import { useMemberV3Flag } from "../context/FeatureContext";

interface MemberBankContextType {
  memberBankInfo: IMemberBankInfo | null;
  isMultiCardHolder: boolean;
  isSummitCardHolder: boolean;
  isBarclaysCardHolder: boolean;
  isSmallBusinessCardHolder: boolean;
  isNonCardHolder: boolean;
  isLoading: boolean;
  hasFreePointSharingNetwork: boolean;
  error: string | null;
}

const MemberBankContext = createContext<
  MemberBankContextType | undefined
>(undefined);

interface MemberBankInfoProviderProps {
  token: string;
  mpNumber: string;
  accountGuid: string;
  children: ReactNode;
}

const MemberBankInfoProvider: React.FC<MemberBankInfoProviderProps> = ({
  token,
  mpNumber,
  accountGuid,
  children,
}) => {
  const baseURL = `${process.env.REACT_APP_APIM_BASE_URL}/1/Retain/MileagePlan/mpchoice/api`;
  const memberInfoUrl = `${baseURL}/Member/GetMemberBankCardDetails`;
  const freePointSharingUrl = `${baseURL}/Member/IsMemberInFreePointSharingNetwork`;

  const [memberBankInfo, setMemberBankData] = useState<IMemberBankInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isMemberInfoV3Enabled = useMemberV3Flag();
  const [isMultiCardHolder, setIsMultiCardHolder] = useState<boolean>(false);
  const [isSummitCardHolder, setIsSummitCardHolder] = useState<boolean>(false);
  const [isBarclaysCardHolder, setIsBarclaysCardHolder] = useState<boolean>(false);
  const [isSmallBusinessCardHolder, setIsSmallBusinessCardHolder] = useState<boolean>(false);
  const [isNonCardHolder, setIsNonCardHolder] = useState<boolean>(false);
  const [hasFreePointSharingNetwork, setHasFreePointSharingNetwork] = useState<boolean>(false);

  useEffect(() => {
    if (!isMemberInfoV3Enabled) {
      setIsLoading(false);
      return; //Don't render anything if the member info v3 feature is not enabled
    }

    const fetchData = async () => {
      try {
        const response = await fetch(
          memberInfoUrl as string,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              mpNumber: mpNumber,
              accountGuid: accountGuid,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch member bank details");
        }

        const json = await response.json();
        setMemberBankData(json);
        setIsLoading(false);
      } catch (err: any) {
        setError(err.message || "Unknown error");
        setIsLoading(false);
      }
    };

    //get free point sharing details
    const fetchFreePointSharingDetails = async () => {
      try {
        const urlWithParams = `${freePointSharingUrl}?mpNumber=${encodeURIComponent(mpNumber)}`;
        const response = await fetch(
          urlWithParams,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            }

          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch free point sharing details");
        }

        const json = await response.json();
        setHasFreePointSharingNetwork(json);
        setIsLoading(false);
      } catch (err: any) {
        setError(err.message || "Unknown error");
        setIsLoading(false);
      }
    };


    fetchData();
    fetchFreePointSharingDetails();
  }, [isMemberInfoV3Enabled, mpNumber, token]);

  useEffect(() => {
    // Check if the user is a Summit card holder
    const hasSummitCard = memberBankInfo?.creditCardDetails?.some(
      (card) => card.tsysCode === BankCardCodeType.Summit
    );

    // Check if the user has any non-Summit cards
    const hasOtherCards = memberBankInfo?.creditCardDetails?.some(
      (card) => card.tsysCode !== BankCardCodeType.Summit
    );

    // Check if the user has a Barclays card
    const hasBarclaysCard = memberBankInfo?.creditCardDetails?.some(
      (card) => BankCardCodeType.Barclays.includes(card.tsysCode as typeof BankCardCodeType.Barclays[number])
    );

    // Check if the user has a Small Business card
    const hasSmallBusinessCard = memberBankInfo?.creditCardDetails?.some(
      (card) => card.tsysCode === BankCardCodeType.SmallBusiness
    );

    // Multi-card holder = has Summit + at least one other card
    if (memberBankInfo?.creditCardDetails && hasSummitCard && hasOtherCards) {
      setIsMultiCardHolder(true);
    } else {
      setIsMultiCardHolder(false);
    }

    // Summit card holder check
    setIsSummitCardHolder(!!hasSummitCard);

    // Barclays card holder check
    setIsBarclaysCardHolder(!!hasBarclaysCard);

    // Small Business card holder check
    setIsSmallBusinessCardHolder(!!hasSmallBusinessCard);

    const hasNoBankCardDetails = !memberBankInfo?.creditCardDetails || memberBankInfo.creditCardDetails.length === 0;
    if (hasNoBankCardDetails || (!hasSummitCard && !hasBarclaysCard && !hasSmallBusinessCard && !isMultiCardHolder)) {
      setIsNonCardHolder(true);
    } else {
      setIsNonCardHolder(false);
    }

  }, [memberBankInfo]);


  return (
    <MemberBankContext.Provider value={{ memberBankInfo, isMultiCardHolder, isBarclaysCardHolder, isSmallBusinessCardHolder, isSummitCardHolder, isNonCardHolder, hasFreePointSharingNetwork, isLoading, error }}>
      {children}
    </MemberBankContext.Provider>
  );
};

const useMemberBankInfo = () => {
  const context = useContext(MemberBankContext);
  if (context === undefined) {
    throw new Error(
      "useMemberBankInfo must be used within an MemberBankInfoProvider"
    );
  }
  return context;
};

export { MemberBankInfoProvider, useMemberBankInfo };
